/**
 * JSON to Graph Converter
 *
 * Converts workflow JSON to React Flow compatible graph structure.
 * Handles both full and simplified formats.
 */

import type { Node, Edge } from '@xyflow/react';
import {
  WorkflowJSON,
  SimplifiedWorkflowJSON,
  WorkflowNodeJSON,
  WorkflowEdgeJSON,
  detectWorkflowFormat,
  WorkflowJSONFormat,
} from '../types';
import { convertSimplifiedToFull } from './simplifiedToFullConverter';
import { fillMissingPositions } from './autoLayoutService';

/**
 * Options for JSON to graph conversion
 */
export interface JsonToGraphOptions {
  /** Auto-layout nodes if positions missing (default: true) */
  autoLayout?: boolean;
  /** Auto-generate missing IDs (default: true) */
  autoGenerateIds?: boolean;
}

/**
 * Result of JSON to graph conversion
 */
export interface JsonToGraphResult {
  nodes: Node[];
  edges: Edge[];
  workflowJSON: WorkflowJSON;
}

/**
 * Convert any workflow JSON to React Flow graph
 */
export function convertJsonToGraph(
  json: WorkflowJSON | SimplifiedWorkflowJSON | string,
  options: JsonToGraphOptions = {}
): JsonToGraphResult {
  const { autoLayout = true, autoGenerateIds = true } = options;

  // Parse if string
  let parsedJson: WorkflowJSON | SimplifiedWorkflowJSON;
  if (typeof json === 'string') {
    parsedJson = JSON.parse(json);
  } else {
    parsedJson = json;
  }

  // Detect format and convert if needed
  const format = detectWorkflowFormat(parsedJson);
  let workflowJSON: WorkflowJSON;

  if (format === 'simplified') {
    workflowJSON = convertSimplifiedToFull(parsedJson as SimplifiedWorkflowJSON, {
      autoLayout,
    });
  } else {
    workflowJSON = parsedJson as WorkflowJSON;
  }

  // Fill missing positions if auto-layout enabled
  let nodes = workflowJSON.nodes;
  if (autoLayout) {
    nodes = fillMissingPositions(workflowJSON.nodes, workflowJSON.edges);
  }

  // Convert to React Flow format
  const reactFlowNodes = convertNodesToReactFlow(nodes);
  const reactFlowEdges = convertEdgesToReactFlow(workflowJSON.edges);

  return {
    nodes: reactFlowNodes,
    edges: reactFlowEdges,
    workflowJSON: {
      ...workflowJSON,
      nodes,
    },
  };
}

/**
 * Convert workflow nodes to React Flow nodes
 */
export function convertNodesToReactFlow(nodes: WorkflowNodeJSON[]): Node[] {
  return nodes.map((node) => ({
    id: node.id,
    type: node.type,
    position: node.position,
    data: {
      ...node.data,
      // Ensure nodeType is set
      nodeType: node.data?.nodeType || node.type,
    },
  }));
}

/**
 * Convert workflow edges to React Flow edges
 */
export function convertEdgesToReactFlow(edges: WorkflowEdgeJSON[]): Edge[] {
  return edges.map((edge) => ({
    id: edge.id,
    source: edge.source,
    target: edge.target,
    sourceHandle: edge.sourceHandle,
    targetHandle: edge.targetHandle,
  }));
}

/**
 * Convert React Flow graph back to workflow JSON
 */
export function convertGraphToJson(
  nodes: Node[],
  edges: Edge[],
  metadata: Partial<WorkflowJSON> = {}
): WorkflowJSON {
  const workflowNodes: WorkflowNodeJSON[] = nodes.map((node) => ({
    id: node.id,
    type: node.type || (node.data as Record<string, unknown>)?.nodeType as string || 'unknown',
    position: node.position,
    data: {
      nodeType: (node.data as Record<string, unknown>)?.nodeType as string || node.type || 'unknown',
      label: (node.data as Record<string, unknown>)?.label as string,
      name: (node.data as Record<string, unknown>)?.name as string,
      configured: (node.data as Record<string, unknown>)?.configured as boolean ?? false,
      parameters: (node.data as Record<string, unknown>)?.parameters as Record<string, unknown>,
      ...(node.data as Record<string, unknown>),
    },
  }));

  const workflowEdges: WorkflowEdgeJSON[] = edges.map((edge) => ({
    id: edge.id,
    source: edge.source,
    target: edge.target,
    sourceHandle: edge.sourceHandle || undefined,
    targetHandle: edge.targetHandle || undefined,
  }));

  return {
    version: '1.0',
    playbookName: metadata.playbookName || 'Untitled Workflow',
    description: metadata.description,
    entityType: metadata.entityType || 'CONTACT',
    status: metadata.status || 'DRAFT',
    templateType: metadata.templateType,
    triggerType: metadata.triggerType,
    nodes: workflowNodes,
    edges: workflowEdges,
    metadata: metadata.metadata,
  };
}

/**
 * Detect format and parse workflow JSON
 */
export function parseWorkflowJson(
  input: string | object
): { format: WorkflowJSONFormat; json: WorkflowJSON | SimplifiedWorkflowJSON } {
  const parsed = typeof input === 'string' ? JSON.parse(input) : input;
  const format = detectWorkflowFormat(parsed);

  return {
    format,
    json: parsed as WorkflowJSON | SimplifiedWorkflowJSON,
  };
}
